import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BellRing, BellOff, Smartphone, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";


function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export default function AdminAlerts() {
  const { t } = useLanguage();
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const [on, setOn] = useState(false);
  const [devices, setDevices] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  const refresh = async () => {
    try {
      let endpoint = "";
      if (supported) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        endpoint = sub?.endpoint || "";
      }
      const { data } = await api.get("/push/status", { params: { endpoint } });
      setOn(data.this_device);
      setDevices(data.devices);
    } catch (err) { console.warn("Push status check failed", err); }
    setChecked(true);
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enable = async () => {
    setLoading(true);
    try {
      const { data: vapid } = await api.get("/push/vapid-key");
      if (!vapid.publicKey) throw new Error("Push not configured");
      const permission = await Notification.requestPermission();
      if (permission !== "granted") { toast.error(t("alerts.denied")); return; }
      const reg = await navigator.serviceWorker.ready;
      const sub = (await reg.pushManager.getSubscription()) || await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.publicKey),
      });
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      toast.success(t("alerts.enabled"));
      await refresh();
    } catch (err) {
      console.error(err);
      toast.error(t("alerts.enable_fail"));
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      const endpoint = sub?.endpoint || "";
      if (sub) await sub.unsubscribe();
      await api.post("/push/unsubscribe", { endpoint });
      toast.success(t("alerts.disabled"));
      await refresh();
    } catch {
      toast.error(t("alerts.disable_fail"));
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async () => {
    const { data } = await api.post("/push/test");
    data.sent ? toast.success(t("alerts.test_sent")) : toast.error(t("alerts.test_fail"));
  };

  const needsInstall = isIOS() && !isStandalone();

  return (
    <div className={`rounded-3xl border p-5 sm:p-6 mb-6 ${on ? "border-coral/40 bg-coral/5" : "border-border/50 bg-card"}`} data-testid="admin-alerts-card">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${on ? "bg-coral text-white" : "bg-muted text-muted-foreground"}`}>
          {on ? <BellRing size={26} /> : <BellOff size={26} />}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading font-bold text-lg sm:text-xl" data-testid="admin-alerts-title">
            {t("alerts.title")} <span className={on ? "text-coral" : "text-muted-foreground"} data-testid="admin-alerts-status">{checked ? (on ? t("alerts.on") : t("alerts.off")) : "…"}</span>
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t("alerts.desc")}
            {devices > 0 && <span className="ml-1" data-testid="admin-alerts-devices">{t("alerts.devices", { n: devices })}</span>}
          </p>
          {!supported && <p className="text-sm text-destructive mt-2" data-testid="admin-alerts-unsupported">{t("alerts.unsupported")}</p>}
          {supported && needsInstall && !on && (
            <p className="text-sm mt-2 flex items-start gap-2 text-foreground/80" data-testid="admin-alerts-ios-hint">
              <Smartphone size={16} className="shrink-0 mt-0.5" /> {t("alerts.ios_hint")}
            </p>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          {supported && !on && (
            <Button onClick={enable} disabled={loading || !checked} size="lg" className="rounded-2xl bg-coral hover:bg-coral/90 text-white text-base px-6 h-12 gap-2" data-testid="admin-alerts-enable-btn">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <BellRing size={18} />} {t("alerts.enable")}
            </Button>
          )}
          {supported && on && (
            <>
              <Button onClick={sendTest} variant="outline" size="lg" className="rounded-2xl text-base h-12 gap-2" data-testid="admin-alerts-test-btn">
                <CheckCircle2 size={18} /> {t("alerts.test")}
              </Button>
              <Button onClick={disable} disabled={loading} variant="ghost" size="lg" className="rounded-2xl text-base h-12 text-muted-foreground" data-testid="admin-alerts-disable-btn">
                {t("alerts.disable")}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
